import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

import styled from "@emotion/styled";
import { animate, AnimationPlaybackControls } from "motion";

import { Box } from "@/components/layout/flexbox";
import PaginationButton from "@/components/pieces/form/PaginationButton";

interface CarouselProps {
  children: React.ReactElement[];
  className?: string;
  paginationClassName?: string;
  circular?: boolean;
  paginatorButtonStyle?: CSSProperties;
  pageInfo?: ReactNode;
  onPageChange?: (page: number) => void;
}

export default function Carousel({
  children,
  className,
  paginationClassName,
  circular = false,
  paginatorButtonStyle,
  pageInfo,
  onPageChange,
}: CarouselProps) {
  const [[page, direction], setPage] = useState([1, 0]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [containerHeight, setContainerHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<AnimationPlaybackControls | null>(null);
  const resizeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pageSize = 1;
  const totalPage = Math.ceil(children.length / pageSize);
  const index = (page - 1) * pageSize;

  useEffect(() => {
    if (!containerRef.current) return;
    const elements = containerRef.current.children;
    if (!elements.length) return;

    const updateHeight = () => {
      // Clear any pending timeout
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // Debounce the height update
      resizeTimeoutRef.current = setTimeout(() => {
        const currentElement = elements[0] as HTMLElement;
        const newHeight = currentElement.offsetHeight;

        // Only update if the height has actually changed
        if (newHeight !== containerHeight && newHeight > 0) {
          // Use RAF to batch height updates
          requestAnimationFrame(() => {
            setContainerHeight(newHeight);
          });
        }
      }, 100); // 100ms debounce
    };

    const resizeObserver = new ResizeObserver(() => {
      // Wrap in RAF to avoid loop limit exceeded
      requestAnimationFrame(() => {
        updateHeight();
      });
    });

    // Observe only the first child
    resizeObserver.observe(elements[0]);

    return () => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [page, children, containerHeight]);

  useEffect(() => {
    if (!containerRef.current) return;

    const elements = containerRef.current.children;
    if (!elements.length) return;

    const currentElement = elements[0] as HTMLElement;

    if (animationRef.current) {
      animationRef.current.cancel();
    }

    setIsAnimating(true);

    currentElement.style.transform = `translateX(${direction * 1000}px)`;
    currentElement.style.opacity = "0";

    const animation = animate(
      currentElement as HTMLElement,
      {
        transform: "translateX(0px)",
        opacity: 1,
      },
      {
        duration: 0.6,
        ease: [0.22, 0.03, 0.26, 1],
        onComplete: () => {
          setIsAnimating(false);

          if (circular) {
            if (page > totalPage) {
              setPage([1, 0]);
            } else if (page < 1) {
              setPage([totalPage, 0]);
            }
          }
        },
      },
    );

    animationRef.current = animation;

    return () => {
      if (animationRef.current) {
        animationRef.current.cancel();
      }
    };
  }, [page, direction, circular, totalPage]);

  const onNext = () => {
    if (isAnimating) return;
    if (!circular && page >= totalPage) return;
    setPage([page + 1, 1]);
    onPageChange?.(page + 1);
  };

  const onPrev = () => {
    if (isAnimating) return;
    if (!circular && page <= 1) return;
    setPage([page - 1, -1]);
    onPageChange?.(page - 1);
  };

  const getDisplayIndex = () => {
    if (circular) {
      if (page > totalPage) return 0;
      if (page < 1) return totalPage - 1;
    }
    return index;
  };

  const isPrevDisabled = !circular && page <= 1;
  const isNextDisabled = !circular && page >= totalPage;

  const buttonStyle = {
    paddingLeft: 10,
    paddingRight: 10,
    ...paginatorButtonStyle,
  };

  return (
    <CarouselContainer className={className}>
      <ContentWrapper
        style={{
          height: containerHeight,
          transition: "height 0.3s ease",
        }}
      >
        <div
          ref={containerRef}
          style={{
            position: "relative",
            width: "100%",
          }}
        >
          {children
            .slice(getDisplayIndex(), getDisplayIndex() + pageSize)
            .map((child, i) => (
              <div
                key={`slide-${getDisplayIndex() + i}`}
                style={{
                  position: "absolute",
                  width: "100%",
                  left: 0,
                  top: 0,
                  willChange: "transform, opacity",
                }}
              >
                {child}
              </div>
            ))}
        </div>
      </ContentWrapper>

      <PaginationWrapper className={paginationClassName}>
        <Box mx={2}>
          <PaginationButton
            icon="chevron-left"
            disabled={isPrevDisabled}
            onClick={onPrev}
            style={buttonStyle}
          />
        </Box>
        {pageInfo && <Box mx={2}>{pageInfo}</Box>}
        <Box>
          <PaginationButton
            icon="chevron-right"
            disabled={isNextDisabled}
            onClick={onNext}
            style={buttonStyle}
          />
        </Box>
      </PaginationWrapper>
    </CarouselContainer>
  );
}

const CarouselContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  position: relative;
  width: "100%";
  overflow: hidden;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0;
`;
